<template>
    <main>
        <div
            class="overflow-hidden py-12 sm:py-24 shadow-[0px_-7px_15px_-9px_rgba(0,_0,_0,_0.55)]"
            id="specs"
        >
            <div class="mx-auto max-w-4xl md:px-6 lg:px-8">
                <div class="grid grid-cols-1 gap-x-8 lg:items-start">
                    <div class="px-6 md:px-0 lg:pr-4 lg:pt-4">
                        <h1 class="text-center">
                            {{ trans("components.specs.title") }}
                        </h1>
                        <p class="text-center py-4" v-if="desk">
                            {{ getTranslatedValue(desk, "description") }}
                        </p>
                        <div
                            class="sm:mx-auto sm:max-w-2xl sm:rounded-3xl lg:mx-0 lg:max-w-none"
                        >
                            <div
                                class="mx-auto max-w-2xl sm:mx-0 sm:max-w-none"
                            >
                                <div
                                    v-for="(tabContent, index) in tabContents"
                                    :key="index"
                                    tabindex="0"
                                    class=""
                                >
                                    <div
                                        @click="debouncedToggle(index)"
                                        class="title my-8 text-xl font-medium cursor-pointer flex justify-between"
                                    >
                                        <span> {{ tabContent.name }}</span>
                                        <transition name="collapse">
                                            <i
                                                :class="{
                                                    'fa-plus': !isOpen(index),
                                                    'fa-minus': isOpen(index),
                                                }"
                                                class="fa-solid"
                                            ></i>
                                        </transition>
                                    </div>
                                    <div v-if="isOpen(index)" class="content">
                                        <div class="overflow-x-auto">
                                            <table class="table">
                                                <tbody>
                                                    <tr
                                                        v-for="(
                                                            item, idx
                                                        ) in tabContent.items"
                                                        :key="idx"
                                                        v-memo="[
                                                            item.name,
                                                            item.value,
                                                        ]"
                                                    >
                                                        <td>
                                                            <b>{{
                                                                item.name
                                                            }}</b>
                                                        </td>
                                                        <td>
                                                            {{ item.value }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import {computed, ref, watch, watchEffect} from "vue";
import { debounce } from "lodash-es";
import { trans } from "laravel-vue-i18n";
import { getTranslatedValue } from "@/Utils/TranslationUtils";

const props = defineProps([
    "size",
    "deskMaterial",
    "frameMaterial",
    "desk",
    "deskRadius",
]);
const openIndexes = ref([0]); // Open the first tab by default

const tabContents = ref([]);

const toggle = (index) => {
    const i = openIndexes.value.indexOf(index);
    if (i > -1) {
        openIndexes.value.splice(i, 1);
    } else {
        openIndexes.value.push(index);
    }
};

const debouncedToggle = debounce(toggle, 50);

const isOpen = (index) => {
    return openIndexes.value.includes(index);
};

const tabletopMaterial = computed(() => {
    if (props.desk.slug === 'level-one') {
        return trans("components.product_type_one.tabletop_ext");
    }
    return trans("components.product_type_two.tabletop_ext");
});

watchEffect(() => {
    tabContents.value = [
        {
            name: trans("components.specs.dimensions_details"),
            items: [
                {
                    name: trans("components.specs.width"),
                    value: props.size.width + "cm",
                },
                {
                    name: trans("components.specs.depth"),
                    value: props.size.depth + "cm",
                },
                { name: trans("components.specs.max_load"), value: "200kg" },
                { name: trans("components.specs.min_height"), value: "63cm" },
                { name: trans("components.specs.max_height"), value: "128cm" },
            ],
        },
        {
            name: trans("components.specs.materials"),
            items: [
                {
                    name: trans("components.specs.tabletop_color"),
                    value: props.deskMaterial.name,
                },
                {
                    name: trans("components.specs.tabletop_material"),
                    value: tabletopMaterial,
                },
                {
                    name: trans("components.specs.tabletop_finish"),
                    value: trans("components.specs.tabletop_finish_value"),
                },
                {
                    name: trans("components.specs.frame_color"),
                    value: props.frameMaterial.name,
                },
                {
                    name: trans("components.specs.frame_material"),
                    value: trans("components.specs.frame_material_value"),
                },
                {
                    name: trans("components.specs.frame_finish"),
                    value: trans("components.specs.frame_finish_value"),
                },
            ],
        },
        {
            name: trans("components.specs.delivery_assembly"),
            items: [
                {
                    name: trans("components.specs.safe_delivery"),
                    value: trans("components.specs.safe_delivery_value"),
                },
                {
                    name: trans("components.specs.in_the_box"),
                    value: trans("components.specs.in_the_box_value"),
                },
                {
                    name: trans("components.specs.easy_assembly"),
                    value: trans("components.specs.easy_assembly_value"),
                },
            ],
        },
    ];
});
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
    transition: max-height 0.1s ease;
}

.collapse-enter, .collapse-leave-to /* .collapse-leave-active in <2.1.8 */ {
    max-height: 0;
    overflow: hidden;
}

.content {
    max-height: 500px; /* Adjust according to content */
}
</style>
